import React, { useEffect, useState } from "react";
import { api, CHF } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Upload, ArrowUp, ArrowDown } from "lucide-react";

const empty = { name: "", category: "", description: "", price: 0, image: "", available: true, sort_order: 0, addon_groups: [] };

export default function ProductsTab({ onChanged }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [copySource, setCopySource] = useState("");
  const [copyTargets, setCopyTargets] = useState([]);
  const [showCopy, setShowCopy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState([]);

  const [catList, setCatList] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const load = () => api.get("/products").then((r) => setProducts(r.data));
  const loadCats = () => api.get("/categories").then((r) => setCatList(r.data));
  useEffect(() => { load(); loadCats(); }, []);

  const addCategory = async () => {
    const n = newCatName.trim();
    if (!n) return;
    await api.post("/categories", { name: n });
    setNewCatName(""); loadCats(); toast.success("Catégorie ajoutée");
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ? (les produits existants gardent leur catégorie)`)) return;
    await api.delete(`/categories/${c.id}`); loadCats();
  };
  const reorderCat = async (idx, dir) => {
    const arr = [...catList]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setCatList(arr);
    await api.put("/categories/reorder", { ids: arr.map((c) => c.id) });
  };
  const [moveCat, setMoveCat] = useState("");
  const moveSelected = async () => {
    if (!moveCat) return toast.error("Choisissez une catégorie");
    if (selected.length === 0) return;
    await api.post("/products/bulk-category", { ids: selected, category: moveCat });
    toast.success(`${selected.length} produit(s) déplacé(s) vers ${moveCat}`);
    setSelected([]); load(); onChanged?.();
  };

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); };

  const save = async () => {
    if (!form.name || !form.category) return toast.error("Nom et catégorie requis");
    const payload = { ...form, price: parseFloat(form.price) || 0, sort_order: parseInt(form.sort_order) || 0 };
    try {
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Produit enregistré");
      setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Supprimé"); load(); onChanged?.();
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = products.length > 0 && selected.length === products.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : products.map((p) => p.id));

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer ${selected.length} produit(s) sélectionné(s) ?`)) return;
    const { data } = await api.post("/products/bulk-delete", { ids: selected });
    toast.success(`${data.deleted} produit(s) supprimé(s)`);
    setSelected([]); load(); onChanged?.();
  };

  const deleteAll = async () => {
    if (products.length === 0) return;
    if (!window.confirm(`⚠️ Supprimer TOUS les produits (${products.length}) ? Cette action est irréversible.`)) return;
    const { data } = await api.delete("/products/all");
    toast.success(`${data.deleted} produit(s) supprimé(s)`);
    setSelected([]); load(); onChanged?.();
  };

  // addon group editing
  const addGroup = () => setForm((f) => ({ ...f, addon_groups: [...f.addon_groups, { id: `g${Date.now()}`, name: "", type: "radio", required: false, min: 0, max: 0, options: [] }] }));
  const updGroup = (gi, patch) => setForm((f) => ({ ...f, addon_groups: f.addon_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const delGroup = (gi) => setForm((f) => ({ ...f, addon_groups: f.addon_groups.filter((_, i) => i !== gi) }));
  const addOpt = (gi) => updGroup(gi, { options: [...form.addon_groups[gi].options, { id: `o${Date.now()}`, name: "", price: 0 }] });
  const updOpt = (gi, oi, patch) => updGroup(gi, { options: form.addon_groups[gi].options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });
  const delOpt = (gi, oi) => updGroup(gi, { options: form.addon_groups[gi].options.filter((_, i) => i !== oi) });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(String(r.result || ""));
    r.readAsText(f);
  };

  const doImport = async () => {
    if (!csvText.trim()) return toast.error("Collez un CSV ou choisissez un fichier");
    setImporting(true);
    try {
      const { data } = await api.post("/products/import-csv", { csv_text: csvText });
      toast.success(`${data.created} produit(s) importé(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} ligne(s) ignorée(s)`);
      setShowImport(false); setCsvText(""); load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setImporting(false); }
  };

  const doCopy = async () => {
    if (!copySource || copyTargets.length === 0) return toast.error("Choisissez source et cibles");
    await api.post("/products/copy-addons", { source_id: copySource, target_ids: copyTargets });
    toast.success("Addons copiés"); setShowCopy(false); setCopyTargets([]); load();
  };

  const present = [...new Set(products.map((p) => p.category))];
  const cats = [...catList.map((c) => c.name).filter((n) => present.includes(n)), ...present.filter((n) => !catList.some((c) => c.name === n))];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-2xl font-bold">Produits ({products.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowImport(true)} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10" data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-2" />Importer CSV</Button>
          <Button onClick={() => setShowCopy(true)} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10" data-testid="copy-addons-btn"><Copy className="w-4 h-4 mr-2" />Copier addons</Button>
          <Button onClick={deleteAll} variant="outline" className="rounded-none border-red-500/50 text-red-400 bg-transparent hover:bg-red-500/10" data-testid="delete-all-btn"><Trash2 className="w-4 h-4 mr-2" />Tout supprimer</Button>
          <Button onClick={openNew} className="rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2" />Nouveau</Button>
        </div>
      </div>

      <div className="border border-white/15 p-3 mb-4" data-testid="category-manager">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm uppercase tracking-widest text-white/50">Catégories</span>
        </div>
        <div className="flex gap-2 mb-3">
          <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder="Nouvelle catégorie" className="rounded-none bg-neutral-800 border-white/20 h-9" data-testid="new-category-input" />
          <Button onClick={addCategory} className="rounded-none bg-brand-red hover:bg-brand-red/90 h-9" data-testid="add-category-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {catList.map((c, idx) => (
            <span key={c.id} className="flex items-center gap-1 text-xs border border-white/20 px-2 py-1" data-testid={`cat-chip-${c.name}`}>
              <button onClick={() => reorderCat(idx, -1)} disabled={idx === 0} className="text-white/50 hover:text-white disabled:opacity-20" data-testid={`cat-up-${c.name}`}><ArrowUp className="w-3 h-3" /></button>
              <button onClick={() => reorderCat(idx, 1)} disabled={idx === catList.length - 1} className="text-white/50 hover:text-white disabled:opacity-20" data-testid={`cat-down-${c.name}`}><ArrowDown className="w-3 h-3" /></button>
              {c.name}
              <button onClick={() => removeCategory(c)} className="text-red-400 ml-1" data-testid={`del-cat-${c.name}`}><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
          {catList.length === 0 && <span className="text-xs text-white/40">Aucune catégorie — ajoutez-en une.</span>}
        </div>
      </div>

      {products.length > 0 && (
        <div className="flex items-center justify-between border border-white/15 bg-neutral-900 px-3 py-2 mb-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="select-all-label">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="select-all-checkbox" className="w-4 h-4" />
            Tout sélectionner ({selected.length}/{products.length})
          </label>
          {selected.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={moveCat} onChange={(e) => setMoveCat(e.target.value)} className="rounded-none bg-neutral-800 border border-white/20 px-2 h-8 text-sm" data-testid="bulk-move-category">
                <option value="">Déplacer vers…</option>
                {catList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <Button onClick={moveSelected} className="rounded-none bg-brand-red hover:bg-brand-red/90 h-8" data-testid="bulk-move-btn"><ArrowDown className="w-4 h-4 mr-1" />Déplacer ({selected.length})</Button>
              <Button onClick={deleteSelected} className="rounded-none bg-red-700 hover:bg-red-800 h-8" data-testid="delete-selected-btn"><Trash2 className="w-4 h-4 mr-2" />Supprimer ({selected.length})</Button>
            </div>
          )}
        </div>
      )}

      {cats.map((c) => (
        <div key={c} className="mb-6">
          <h3 className="text-sm uppercase tracking-widest text-white/50 mb-2">{c}</h3>
          <div className="space-y-2">
            {products.filter((p) => p.category === c).map((p) => (
              <div key={p.id} className={`flex items-center gap-3 border p-2 ${selected.includes(p.id) ? "border-brand-red bg-brand-red/5" : "border-white/15"}`} data-testid={`admin-product-${p.id}`}>
                <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} data-testid={`select-product-${p.id}`} className="w-4 h-4 shrink-0" />
                {p.image && <img src={p.image} alt="" className="w-12 h-12 object-cover" />}
                <div className="flex-1">
                  <p className="font-medium">{p.name} {!p.available && <span className="text-xs text-brand-red">(indispo)</span>}</p>
                  <p className="text-xs text-white/50">{CHF(p.price)} · {p.addon_groups?.length || 0} groupe(s) d'addons</p>
                </div>
                <Button onClick={() => openEdit(p)} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10 h-8 w-8 p-0" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button onClick={() => del(p)} variant="outline" className="rounded-none border-red-500/40 text-red-400 bg-transparent hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`del-product-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90dvh] flex flex-col rounded-none border-2 border-white bg-neutral-900 text-white p-0">
          <DialogHeader className="p-5 border-b border-white/15"><DialogTitle className="font-display text-2xl">{editing === "new" ? "Nouveau produit" : "Modifier"}</DialogTitle></DialogHeader>
          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="pf-name" /></div>
              <div><Label>Catégorie</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-none bg-neutral-800 border border-white/20 px-2 py-2 text-sm" data-testid="pf-category">
                  <option value="">— Choisir une catégorie —</option>
                  {catList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-white/40 mt-1">Ajoutez de nouvelles catégories dans la section « Catégories » en haut.</p>
              </div>
              <div><Label>Prix (CHF)</Label><Input type="number" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="pf-price" /></div>
              <div><Label>Ordre</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" /></div>
            </div>
            <div><Label>Image (URL)</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="pf-image" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 resize-none" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="pf-available" /><Label>Disponible</Label></div>

            <div className="border-t border-white/15 pt-3">
              <div className="flex justify-between items-center mb-2"><Label>Groupes d'addons</Label><Button onClick={addGroup} variant="outline" className="rounded-none border-white/30 bg-transparent h-7 text-xs" data-testid="add-group-btn"><Plus className="w-3 h-3 mr-1" />Groupe</Button></div>
              {form.addon_groups.map((g, gi) => (
                <div key={g.id} className="border border-white/15 p-3 mb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Nom du groupe" value={g.name} onChange={(e) => updGroup(gi, { name: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" />
                    <select value={g.type} onChange={(e) => updGroup(gi, { type: e.target.value })} className="rounded-none bg-neutral-800 border border-white/20 px-2 text-sm">
                      <option value="radio">Choix unique (radio)</option>
                      <option value="checkbox">Choix multiple (checkbox)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={g.required} onChange={(e) => updGroup(gi, { required: e.target.checked })} />Obligatoire</label>
                    <label className="flex items-center gap-1">Max <input type="number" value={g.max} onChange={(e) => updGroup(gi, { max: parseInt(e.target.value) || 0 })} className="w-14 bg-neutral-800 border border-white/20 px-1" /></label>
                    <button onClick={() => delGroup(gi)} className="text-red-400 ml-auto"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {g.options.map((o, oi) => (
                    <div key={o.id} className="flex gap-2 items-center">
                      <Input placeholder="Option" value={o.name} onChange={(e) => updOpt(gi, oi, { name: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-8" />
                      <Input type="number" step="0.5" placeholder="+CHF" value={o.price} onChange={(e) => updOpt(gi, oi, { price: parseFloat(e.target.value) || 0 })} className="rounded-none bg-neutral-800 border-white/20 h-8 w-24" />
                      <button onClick={() => delOpt(gi, oi)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button onClick={() => addOpt(gi)} variant="outline" className="rounded-none border-white/20 bg-transparent h-7 text-xs w-full">+ Option</Button>
                </div>
              ))}
            </div>
          </div>
          <div className="p-5 border-t border-white/15 flex gap-2">
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-none border-white/30 bg-transparent flex-1">Annuler</Button>
            <Button onClick={save} className="rounded-none bg-brand-red hover:bg-brand-red/90 flex-1" data-testid="save-product-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg rounded-none border-2 border-white bg-neutral-900 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">Importer des produits (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-white/60">Colonnes : <span className="font-mono">Nom, prix, catégorie</span> — <span className="text-white/40">description et image optionnelles</span>. Séparateur , ou ;</p>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="csv-file-input" />
            <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="Nom,prix,catégorie&#10;Nem au poulet,7.5,Entrées&#10;Pad Thaï,21,Wok" className="rounded-none bg-neutral-800 border-white/20 resize-none h-40 font-mono text-xs" data-testid="csv-textarea" />
            <Button onClick={doImport} disabled={importing} className="w-full rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="do-import-btn">{importing ? "Import…" : "Importer"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy addons dialog */}
      <Dialog open={showCopy} onOpenChange={setShowCopy}>
        <DialogContent className="max-w-lg rounded-none border-2 border-white bg-neutral-900 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">Copier les addons</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Depuis</Label>
              <select value={copySource} onChange={(e) => setCopySource(e.target.value)} className="w-full rounded-none bg-neutral-800 border border-white/20 px-2 py-2 text-sm" data-testid="copy-source">
                <option value="">— Choisir —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><Label>Vers</Label>
              <div className="max-h-52 overflow-y-auto border border-white/15 p-2 space-y-1">
                {products.filter((p) => p.id !== copySource).map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={copyTargets.includes(p.id)} onChange={(e) => setCopyTargets((t) => e.target.checked ? [...t, p.id] : t.filter((x) => x !== p.id))} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={doCopy} className="w-full rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="do-copy-btn">Copier</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
